import { AGENTS_TAB, ANALYSES_TAB, ARTIFACT_TABS, DOCUMENTS_TAB, SCHEDULED_TASKS_TAB, SCOUTS_TAB, type ArtifactTab } from './tabs';
import { parseArtifactKey } from './keys';

/**
 * THE ARTIFACTS PAGE'S ADDRESS (`/artifacts`), as plain validators (no zod: `validateSearch` ships in the entry chunk).
 *
 * - `kind`: one tab (`brand_deal`, `deliverable`, …, `influencer_batch`, `report`); absent is All.
 * - `playbook`: one campaign; absent is every campaign she sees.
 * - `q`: free text on the creator, the artifact and the campaign.
 * - `step`: one phase of the tab's kind (`?kind=deliverable&step=live`: the live posts).
 * - `id`: the artifact open in the right-side panel (`deliverable:340`): a brand deal opens its panel, a contract its
 *   contract, a deliverable its deal's deliverables (scrolled to it).
 * - `part`: with a brand deal's `id`, one of its parts in the panel instead (`brief`, `contract`, `deliverables`) — the
 *   card's three buttons (Kazzy, 13 Oct 2026).
 * - `task`: one scheduled task open in its right-side panel (`?kind=scheduled_tasks&task=601`; its id is Draper v2's).
 * - `doc`: one of her team's documents open in its right-side panel (`?kind=documents&doc=<uuid>`).
 * - `scout`: one Scout open in its right-side panel (`?kind=scouts&scout=12`; its id is Draper v2's).
 * - `agent`: one of the team's agents open in its right-side panel (`?kind=agents&agent=901`).
 */
export interface ArtifactsSearch {
  kind?: Exclude<ArtifactTab, 'all'>;
  playbook?: number;
  q?: string;
  step?: string;
  id?: string;
  part?: PanelPart;
  task?: number;
  doc?: string;
  scout?: number;
  agent?: number;
}

/** A brand deal's three parts, each with its own right-side panel. */
export const PANEL_PARTS = ['brief', 'contract', 'deliverables'] as const;
export type PanelPart = (typeof PANEL_PARTS)[number];

/** What a link may pass. */
export type ArtifactsSearchInput = Partial<Record<keyof ArtifactsSearch, unknown>>;

const positiveInt = (value: unknown): number | undefined => {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : Number.NaN;
  return Number.isInteger(n) && n > 0 ? n : undefined;
};

export function parseArtifactsSearch(input: ArtifactsSearchInput | Record<string, unknown>): ArtifactsSearch {
  const raw = input as Record<string, unknown>;
  const out: ArtifactsSearch = {};
  if (typeof raw.kind === 'string' && raw.kind !== 'all' && ([...ARTIFACT_TABS, SCHEDULED_TASKS_TAB, DOCUMENTS_TAB, ANALYSES_TAB, SCOUTS_TAB, AGENTS_TAB] as readonly string[]).includes(raw.kind)) out.kind = raw.kind as ArtifactsSearch['kind'];
  const playbook = positiveInt(raw.playbook);
  if (playbook) out.playbook = playbook;
  if (typeof raw.q === 'string' && raw.q.length > 0 && raw.q.length <= 200) out.q = raw.q;
  if (typeof raw.step === 'string' && /^[a-z_]{1,32}$/.test(raw.step)) out.step = raw.step;
  if (parseArtifactKey(raw.id)) out.id = raw.id as string;
  // A part belongs to a brand deal's panel: alone, or on any other artifact, it means nothing.
  if (out.id?.startsWith('brand_deal:') && typeof raw.part === 'string' && (PANEL_PARTS as readonly string[]).includes(raw.part)) out.part = raw.part as PanelPart;
  const task = positiveInt(raw.task);
  if (task) out.task = task;
  if (typeof raw.doc === 'string' && /^[0-9a-f-]{36}$/i.test(raw.doc)) out.doc = raw.doc;
  const scout = positiveInt(raw.scout);
  if (scout) out.scout = scout;
  const agent = positiveInt(raw.agent);
  if (agent) out.agent = agent;
  return out;
}

/** An id the address carries: a number, or its digits. */
const idIn = (value: unknown): number | null => {
  const id = typeof value === 'number' ? value : typeof value === 'string' && /^[1-9]\d{0,9}$/.test(value) ? Number(value) : null;
  return id !== null && Number.isSafeInteger(id) && id > 0 ? id : null;
};

/**
 * Draper's address: `/draper?chat=<id>` is the chat on screen (`main`, or a side chat's, a goal's or an agent's id),
 * so Back and Forward move between chats and a link opens one; `tag=<key>` opens it with that artifact already tagged;
 * `analysis`: one of Watson's analyses to open in the frame on the right (Artifacts → Analyses, a link Draper wrote).
 */
export function parseDraperSearch(input: { tag?: unknown; chat?: unknown; analysis?: unknown } | Record<string, unknown>): { tag?: string; chat?: 'main' | number; analysis?: number } {
  const raw = input as Record<string, unknown>;
  const out: { tag?: string; chat?: 'main' | number; analysis?: number } = {};
  if (parseArtifactKey(raw.tag)) out.tag = raw.tag as string;
  const chat = raw.chat === 'main' ? 'main' : positiveInt(raw.chat);
  if (chat) out.chat = chat;
  const analysis = idIn(raw.analysis);
  if (analysis !== null) out.analysis = analysis;
  return out;
}
